/**
 * HTML tags the RText component is allowed to enclose its text content in,
 * ordered from most common to least common.
 *
 * Every entry is a standard HTML element that can directly contain text
 * without changing how that text behaves. Deliberately excluded:
 * - Interactive / behavior-changing tags: a, button, label, summary, option,
 *   textarea, select, details, dialog
 * - Void elements that cannot enclose text: br, wbr, hr, img, input
 * - Tags only valid inside a specific parent: li, dt, dd, td, th, caption,
 *   legend, figcaption, rt, rp, optgroup
 * - Raw-text / metadata tags: script, style, title, template
 * - Live-region / form-output tags that alter assistive-technology behavior:
 *   output
 */
export const ENCLOSING_TAGS = [
  // Common block-level text containers
  'p',
  'div',
  // Generic inline container
  'span',
  // Headings
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  // Common inline emphasis and importance
  'strong',
  'em',
  'b',
  'i',
  'u',
  // Common text presentation
  'small',
  'mark',
  // Preformatted and code text
  'pre',
  'code',
  // Quotations
  'blockquote',
  'q',
  // Subscript and superscript
  'sub',
  'sup',
  // Edits and strikethrough
  's',
  'del',
  'ins',
  // Citations, definitions and abbreviations
  'cite',
  'abbr',
  'dfn',
  // Computer I/O text
  'kbd',
  'samp',
  'var',
  // Machine-readable text
  'time',
  'data',
  // Contact information
  'address',
  // Bidirectional text
  'bdi',
  'bdo',
  // Sectioning and grouping containers
  'figure',
  'article',
  'section',
  'aside',
  'header',
  'footer',
  'main',
  'nav',
] as const;

export type EnclosingTag = (typeof ENCLOSING_TAGS)[number];

/**
 * The tags above whose default CSS display is block-level.
 *
 * Box styles — margin, padding, width, border — behave as authored on these
 * elements, so RText can carry the column's styles on the tag itself and
 * needs no wrapper div (see `resolve-no-container.ts`).
 */
export const BLOCK_ENCLOSING_TAGS: readonly EnclosingTag[] = [
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'pre',
  'blockquote',
  'address',
  'figure',
  'article',
  'section',
  'aside',
  'header',
  'footer',
  'main',
  'nav',
];

/**
 * The tags above that default to `display: inline`, where vertical
 * margin/padding and width/height are ignored by the layout engine. Column
 * box styles must go on a wrapper element instead of on the tag.
 *
 * Kept as an explicit list rather than derived from BLOCK_ENCLOSING_TAGS so
 * adding a tag to ENCLOSING_TAGS forces a deliberate block-or-inline call;
 * the coverage test in `__tests__/enclosing-tags.test.ts` fails otherwise.
 */
export const INLINE_ENCLOSING_TAGS: readonly EnclosingTag[] = [
  'span',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'small',
  'mark',
  'code',
  'q',
  'sub',
  'sup',
  's',
  'del',
  'ins',
  'cite',
  'abbr',
  'dfn',
  'kbd',
  'samp',
  'var',
  'time',
  'data',
  'bdi',
  'bdo',
];

/**
 * Runtime guard for values arriving through componentAttributes (untyped
 * editor-authored data). Unknown values are rejected so a bad persisted value
 * can never render an unexpected element.
 */
export function isEnclosingTag(value: unknown): value is EnclosingTag {
  return (
    typeof value === 'string' &&
    (ENCLOSING_TAGS as readonly string[]).includes(value)
  );
}

/**
 * True when the tag renders as a block-level box. Unknown values count as
 * non-block: the safe answer, because it routes box styles to a wrapper
 * element where they are guaranteed to apply.
 */
export function isBlockEnclosingTag(value: unknown): boolean {
  return (
    isEnclosingTag(value) &&
    (BLOCK_ENCLOSING_TAGS as readonly string[]).includes(value)
  );
}
